import { Button, Form, Input, message, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { login } from "../api/allauth";
import { useAuth, useConfig } from "./AuthContext";
import ProviderList from "./ProviderList";

import { ROUTES } from "../routes";
import styles from "./Accounts.module.css";

const { Title, Text, Link } = Typography;

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/";

    const { auth } = useAuth();
    const config = useConfig();
    const hasProviders = config?.data?.socialaccount?.providers?.length > 0;

    const [loading, setLoading] = useState(false);

    // Redirect if already logged in
    useEffect(() => {
        if (auth?.meta?.is_authenticated) {
            navigate(from, { replace: true });
        }
    }, [auth, from, navigate]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const content = await login({
                email: values.email.trim(),
                password: values.password,
            });

            if (content.status === 200 && content.meta?.is_authenticated) {
                message.success("Login successful!");
                navigate(from, { replace: true });
            } else if (content.status === 400) {
                message.error(content.errors[0].message);
            } else {
                message.error("Unexpected login response");
            }
        } catch (err) {
            console.error(err);
            message.error("Login failed, please try again");
        } finally {
            setLoading(false);
        }
    };

    if (auth === undefined) return <Spin tip="Loading..." />;

    return (
        <div className={styles.card}>
            {/* Logo */}
            <img src="/w5n-logo.svg" alt="Logo" className={styles.logo} />

            {/* Title */}
            <Title level={2} className={styles.title}>Welcome back</Title>
            <Text className={styles.subtitle}>
                Don't have an account?{" "}
                <Link onClick={() => navigate(ROUTES.ACCOUNT.SIGNUP)}>
                    Sign up
                </Link>
            </Text>

            {/* Login Form */}
            <Form layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email" },
                    ]}
                >
                    <Input
                        placeholder="Enter your email address"
                        size="large"
                        autoComplete="username"
                    />
                </Form.Item>

                <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: "Please enter your password" }]}
                >
                    <Input.Password
                        placeholder="Enter your password"
                        size="large"
                        autoComplete="current-password"
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        htmlType="submit"
                        loading={loading}
                        block
                        size="large"
                    >
                        Login
                    </Button>
                </Form.Item>
            </Form>

            {/* Forgot password link */}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link onClick={() => navigate(ROUTES.ACCOUNT.PASSWORD_RESET)}>
                    Forgot your password?
                </Link>
            </div>

            {/* OAuth Providers Section */}
            {hasProviders && (
                <div style={{ marginTop: 24 }}>
                    <ProviderList callbackURL={ROUTES.ACCOUNT.PROVIDER_CALLBACK} />
                </div>
            )}
        </div>
    );
};

export default LoginPage;
